import { Command } from "commander";
import { loginCommand } from "./commands/login.js";
import { deployCommand } from "./commands/deploy.js";
import { lsCommand } from "./commands/ls.js";
import { logsCommand } from "./commands/logs.js";
import { sessionsListCommand, sessionsHistoryCommand } from "./commands/sessions.js";
import { agentsListCommand, agentsPullCommand, agentsPushCommand, agentsSkillsCommand, agentsGrantSkillCommand, agentsCreateCommand } from "./commands/agents.js";
import { serversListCommand } from "./commands/servers.js";
import { envSetCommand, envUnsetCommand } from "./commands/env.js";
import { apiCommand } from "./commands/api.js";
import { rmCommand } from "./commands/rm.js";
import { openCommand } from "./commands/open.js";
import { whoamiCommand } from "./commands/whoami.js";
import { initCommand } from "./commands/init.js";
import { devCommand } from "./commands/dev.js";
import { linkCommand } from "./commands/link.js";
import { pullSchemaCommand } from "./commands/pull.js";
import { orgListCommand, orgUseCommand } from "./commands/org.js";
import { connectionsListCommand, connectionsConnectCommand } from "./commands/connections.js";
import { workspaceInitCommand } from "./commands/workspace.js";
import { executeCommand } from "./commands/execute.js";
import { docsCommand } from "./commands/docs.js";
import { mcpBridgeCommand } from "./commands/mcp-bridge.js";
import { sdkInspectCommand, sdkLoginCommand, sdkInstallCommand } from "./commands/sdk.js";
import {
  skillsAddCommand,
  skillsListCommand,
  skillsRemoveCommand,
} from "./commands/skills.js";
import {
  aiFilesInstallCommand,
  aiFilesUninstallCommand,
} from "./commands/ai-files.js";

export async function runCli() {
  const program = new Command();

  program
    .name("clawnify")
    .description("Deploy apps to Clawnify")
    .version("0.1.0");

  program
    .command("init [dir]")
    .description("Scaffold a new Clawnify project (app, website, or agent)")
    .option("--type <type>", "Project type: app, website, or agent (skips prompt)")
    .option("--name <name>", "Project name (skips prompt)")
    .option("--template <template>", "App template: blank or crud (apps only)")
    .option("--storage", "Enable file uploads (R2) — apps only")
    .option("-y, --yes", "Non-interactive: use all defaults (app, blank, no storage)")
    .option("--no-ai-files", "Skip installing AI files (rules + skills + MCP) after scaffolding")
    .action(initCommand);

  program
    .command("dev [dir]")
    .description("Start local dev server")
    .option("--port <port>", "API server port (default: 8787)")
    .action(devCommand);

  program
    .command("login")
    .description("Authenticate with Clawnify")
    .action(loginCommand);

  program
    .command("deploy [dir]")
    .description("Deploy an app, website, or agent to Clawnify")
    .option("--from <repo>", "Deploy from a GitHub repo (owner/repo)")
    .option("--server <server-id>", "Agent deploy: the server hosting the target agent (saved as a link)")
    .option("--agent <name>", "Agent deploy: the named agent to push to (default: main)")
    .option("--path <subdir>", "Subdirectory within the --from repo that holds the app (monorepo)")
    .option("--name <name>", "Override app name")
    .option("--org <slug-or-id>", "Deploy to a specific org (overrides active org)")
    .option("-y, --yes", "Non-interactive: on slug conflict, update existing app")
    .option("--update-existing", "Non-interactive: on slug conflict, overwrite existing app")
    .option("--create-new", "Non-interactive: on slug conflict, create a new app with the suggested slug")
    .option("--dry-run", "List files that would be uploaded; do not deploy")
    .option("--no-sync", "Skip the post-deploy schema.sql sync into the local working directory")
    .action(deployCommand);

  program
    .command("link <app-id-or-slug>")
    .description("Link the current directory to an existing Clawnify app (accepts id or slug)")
    .option("--dir <dir>", "Directory to link (default: cwd)")
    .option("--org <slug-or-id>", "Restrict the app lookup to a specific org")
    .action(linkCommand);

  // `clawnify pull` — sync deployed-app artifacts back into the local
  // working directory. Currently only `schema` is supported; future
  // subcommands (migrations, manifest, etc.) can slot in here.
  const pull = program
    .command("pull")
    .description("Pull artifacts from a deployed app into the current directory");

  pull
    .command("schema")
    .description("Fetch the deployed app's current schema.sql snapshot into the local repo")
    .option("--from <app-id-or-slug>", "App to fetch from (required)")
    .option("--out <path>", "Where to write (default: ./schema.sql)")
    .option("--force", "Overwrite a locally-modified or uncommitted schema.sql")
    .option("--org <slug-or-id>", "Restrict the app lookup to a specific org")
    .action(pullSchemaCommand);

  const org = program.command("org").description("Manage organizations");
  org.command("list").description("List orgs you belong to").action(orgListCommand);
  org
    .command("use <slug-or-id>")
    .description("Set the active org for global commands (ls, whoami, etc.)")
    .action(orgUseCommand);

  // `clawnify connections` — the org's naming catalog (docs §3.5): canonical
  // integration ids, provider-key names, custom env. The read to make before
  // declaring `requires` — never invent an identifier.
  const connections = program
    .command("connections")
    .description("Org integrations & the naming catalog");
  connections
    .command("list")
    .description("List canonical integration ids, provider keys, and custom env for the active org")
    .option("--json", "Print the full catalog payload (includes docs + spec_url per integration)")
    .action(connectionsListCommand);
  connections
    .command("connect <id>")
    .description("Explain how to connect an integration (OAuth in the dashboard — the CLI/agent can't do it for you)")
    .option("--json", "Print the steps as JSON")
    .action(connectionsConnectCommand);

  // `clawnify workspace` — a monorepo hosting one org's apps. Apps scaffolded
  // inside it land under apps/ and inherit the org on deploy.
  const workspace = program
    .command("workspace")
    .description("Manage a monorepo that hosts an org's apps");
  workspace
    .command("init [dir]")
    .description("Scaffold a workspace monorepo bound to an org")
    .option("--org <slug-or-id>", "Org to bind the workspace to (defaults to active org)")
    .option("--name <name>", "Workspace display name (defaults to the org name)")
    .action(workspaceInitCommand);

  program
    .command("ls")
    .description("List deployed apps")
    .action(lsCommand);

  program
    .command("logs <app-id>")
    .description("Show build logs for an app")
    .action(logsCommand);

  // `clawnify servers` — list the org's agent servers (an org can have several,
  // e.g. an agency managing multiple client boxes). Top of the FDE discovery
  // tree: servers → agents → sessions.
  const servers = program
    .command("servers")
    .description("List the org's agent servers");
  servers
    .command("list", { isDefault: true })
    .description("List the org's agent servers")
    .action(serversListCommand);

  // `clawnify sessions` — the FDE observe verb: list stored sessions across
  // ALL agents on the org's server (main + specialists), and read one
  // session's transcript. See docs/internal/fde-strategy.md.
  const sessions = program
    .command("sessions")
    .description("Observe agent sessions (list + read transcripts)");
  sessions
    .command("list", { isDefault: true })
    .description("List stored sessions across all agents on the server")
    .option("--server <id>", "Target server (default: the org's server, or a linked project)")
    .option("--agent <slug>", "Only this agent's sessions")
    .option("--active <minutes>", "Only sessions active within N minutes")
    .option("--limit <n>", "Max rows (default 100)")
    .option("--json", "Print the raw JSON payload")
    .action(sessionsListCommand);
  sessions
    .command("history <key>")
    .description("Read one session's transcript by key (e.g. agent:dnz---theo:lead:row_8268)")
    .option("--server <id>", "Target server (default: the org's server, or a linked project)")
    .option("--agent <slug>", "Owning agent (else derived from the key prefix)")
    .option("--limit <n>", "Max transcript entries (default 100)")
    .option("--json", "Print the raw JSON payload")
    .action(sessionsHistoryCommand);

  // `clawnify agents` — the FDE's agent-management verbs: list the agents on the
  // server, pull an agent's AGENTS.md/skills/flows to edit, push edits back.
  // See docs/internal/fde-strategy.md.
  const agents = program
    .command("agents")
    .description("Manage agents: list, pull config to edit, push edits back");
  agents
    .command("list", { isDefault: true })
    .description("List the agents on the org's server")
    .option("--server <id>", "Target server (default: the org's server)")
    .action(agentsListCommand);
  agents
    .command("pull [dir]")
    .description("Fetch an agent's AGENTS.md + skills/ + flows/ into a directory to edit")
    .option("--server <id>", "Target server (default: the org's server)")
    .option("--agent <slug>", "Which agent (default: main)")
    .action(agentsPullCommand);
  agents
    .command("push [dir]")
    .description("Push edited AGENTS.md + skills/ + flows/ back (additive; picked up next session)")
    .option("--server <id>", "Target server (default: the linked/org's server)")
    .option("--agent <slug>", "Which agent (default: the pulled agent, else main)")
    .action(agentsPushCommand);
  agents
    .command("skills [agent]")
    .description("Show an agent's effective skill inventory (what it can actually run, not just its workspace)")
    .option("--server <id>", "Target server (default: the org's server)")
    .action(agentsSkillsCommand);
  agents
    .command("grant-skill <skill>")
    .description("Grant an existing (main-authored) skill to one agent (--to) or every agent (--all)")
    .option("--server <id>", "Target server (default: the org's server)")
    .option("--to <agent>", "Grant to one specialist agent")
    .option("--all", "Grant to every agent (shared managed root; includes future agents)")
    .action(agentsGrantSkillCommand);
  agents
    .command("create <slug>")
    .description("Create a new specialist agent (RESTARTS the gateway — needs --yes)")
    .option("--server <id>", "Target server (default: the org's server)")
    .option("--name <name>", "Display name (default: the slug)")
    .option("--description <role>", "The agent's role/specialization (seeded into its AGENTS.md)")
    .option("--yes", "Confirm — proceed with the gateway restart")
    .action(agentsCreateCommand);

  // `clawnify env` — the org's custom environment variables (all agents). Read
  // via `connections list`; set/unset here. NOT for secrets — those go in the
  // dashboard (Settings → Environment Variables / API Keys).
  const env = program
    .command("env")
    .description("Org custom environment variables (read via `connections list`)");
  env
    .command("set <key> <value>")
    .description("Set an org-wide custom env var (NON-SECRET config; restarts the gateway to apply)")
    .action(envSetCommand);
  env
    .command("unset <key>")
    .description("Remove an org-wide custom env var (restarts the gateway to apply)")
    .action(envUnsetCommand);

  // `clawnify api` — raw authenticated request; the escape hatch so the
  // porcelain never has to be complete (see gh api / stripe get).
  program
    .command("api <path>")
    .description("Raw authenticated request to the Clawnify API — for anything without a dedicated command")
    .option("-X, --method <method>", "HTTP method (default GET, or POST when -d is given)")
    .option("-d, --data <json>", "JSON request body")
    .option("--org <slug-or-id>", "Org to scope the request to")
    .action(apiCommand);

  program
    .command("rm <app-id>")
    .description("Delete an app")
    .action(rmCommand);

  program
    .command("open <slug>")
    .description("Open an app in the browser")
    .action(openCommand);

  program
    .command("whoami")
    .description("Show current user info")
    .action(whoamiCommand);

  program
    .command("execute [file]")
    .description("Run TypeScript code on the active org's substrate via mcp.clawnify.com")
    .option("-c, --code <inline>", "Inline code instead of a file path")
    .option("--timeout <seconds>", "Execution timeout in seconds (default 30)")
    .action(executeCommand);

  program
    .command("docs [slug]")
    .description("Fetch the skill-convention README for an app (or org index if slug omitted)")
    .option("--caller <hint>", "Caller density hint: internal | dashboard | external", "external")
    .action(docsCommand);

  program
    .command("mcp")
    .description("Run a stdio MCP bridge that proxies to mcp.clawnify.com (for Claude Desktop / ChatGPT / bash-less clients)")
    .action(mcpBridgeCommand);

  // `clawnify skills` — install Clawnify-curated skills (build-a-clawnify-app, ...)
  // into whichever coding agents have rules dirs in cwd (.claude, .cursor,
  // .openclaw, .codex, Aider CONVENTIONS.md). Skill contents are bundled in
  // the CLI; upgrade by bumping the CLI and re-running.
  const skills = program.command("skills").description("Install Clawnify-curated skills into your coding agent(s)");
  skills.command("list").description("List available skills").action(skillsListCommand);
  skills
    .command("add [name]")
    .description("Install a skill into every detected agent (Claude, Cursor, OpenClaw, Codex, Aider)")
    .option("--all", "Install every available skill")
    .option("--agent <agent>", "Force a specific target (claude | cursor | openclaw | codex | aider)")
    .action((name, opts) => skillsAddCommand(name, opts));
  skills
    .command("remove [name]")
    .description("Remove a previously-installed skill from detected agents")
    .option("--all", "Remove every installed skill")
    .option("--agent <agent>", "Restrict to a specific agent")
    .action((name, opts) => skillsRemoveCommand(name, opts));

  // `clawnify ai-files` — one step to make a project agent-ready: writes
  // the always-on Clawnify rules block into each detected agent's rules
  // file, installs the bundled skills, and wires the MCP server into
  // .mcp.json. Mirrors `npx convex ai-files install`.
  const aiFiles = program
    .command("ai-files")
    .description("Install Clawnify rules + skills + MCP into your coding agent(s)");
  aiFiles
    .command("install")
    .description("Write rules, install skills, and wire the MCP server (defaults to Claude if no agent is detected)")
    .option("--agent <agent>", "Force a specific target (claude | cursor | openclaw | codex | aider)")
    .option("--no-mcp", "Skip writing .mcp.json")
    .action((opts) => aiFilesInstallCommand(opts));
  aiFiles
    .command("uninstall")
    .description("Remove the Clawnify rules block, skills, and MCP server entry")
    .option("--agent <agent>", "Restrict to a specific agent")
    .action((opts) => aiFilesUninstallCommand(opts));

  const sdk = program.command("sdk").description("Inspect and manage the per-org SDK surface");
  sdk
    .command("inspect <slug>")
    .description("Show the typed-procedure README for an app (external density)")
    .option("--caller <hint>", "Caller density hint: internal | dashboard | external", "external")
    .action(sdkInspectCommand);
  sdk
    .command("login")
    .description("Authenticate / rotate the OAuth grant (alias of `clawnify login`)")
    .action(sdkLoginCommand);
  sdk
    .command("install")
    .description("(Deferred) Install @clawnify/sdk locally — pending Phase 5b")
    .action(sdkInstallCommand);

  try {
    await program.parseAsync();
  } catch (err) {
    console.error((err as Error)?.message ?? err);
    process.exitCode = 1;
  }

  // Force a prompt exit once a one-shot command finishes. Node's global
  // `fetch` (undici) keeps HTTP connections alive in a pool, which holds the
  // event loop open for several seconds after the command is actually done —
  // that's the "deploy printed ✓ but the prompt doesn't come back" hang.
  // Long-running commands (`dev`, `mcp`) never resolve `parseAsync`, so they
  // never reach this and keep running. Flush stdout first (the empty write's
  // callback fires after all prior writes drain) so piped/non-TTY output isn't
  // truncated by the exit.
  await new Promise<void>((resolve) => process.stdout.write("", () => resolve()));
  process.exit(process.exitCode ?? 0);
}
